import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api.service';
import { useRealtime } from '../contexts/RealtimeContext';

// Contrat de GET /events/{id}/overview. Les problèmes arrivent sous forme de
// CODE + paramètres : la phrase se compose dans la langue de l'organisateur,
// la règle (qu'est-ce qui manque ?) reste au serveur, en un seul endroit.
export type StepKey = 'categories' | 'wods' | 'registrations' | 'planning' | 'results';
export type StepStatus = 'done' | 'warning' | 'todo';

export interface OverviewStep {
  key: StepKey;
  status: StepStatus;
  counts: Record<string, number | null>;
}

export interface OverviewIssue {
  code: string;
  severity: 'warning' | 'info';
  params: Record<string, any>;
}

export interface EventOverview {
  event: {
    id: string;
    name: string;
    event_type: string;
    status: string;
    slug?: string | null;
    date_start?: string | null;
    date_end?: string | null;
    registration_open?: boolean | null;
    registration_end_date?: string | null;
  };
  metrics: {
    registrations_confirmed: number;
    registrations_capacity: number | null;
    revenue_cents: number;
    heats_total: number;
    heats_published: number;
    teams_total: number;
    volunteers_total: number;
  };
  steps: OverviewStep[];
  issues: OverviewIssue[];
}

export const overviewKey = (eventId: string | null) => ['event-overview', eventId] as const;

/**
 * L'état de préparation d'un événement. Partagé par la page d'accueil de
 * l'événement ET le menu (pastilles « à régler ») : une seule requête, un seul
 * cache, et le temps réel l'invalide pour les deux à la fois.
 */
export function useEventOverview(eventId: string | null) {
  const qc = useQueryClient();
  // Tout ce qui peut changer un compteur ou faire apparaître un problème.
  useRealtime(
    ['team', 'heat', 'heat_participant', 'wod', 'category', 'registration', 'crossfit_result'],
    () => { if (eventId) qc.invalidateQueries({ queryKey: overviewKey(eventId) }); },
  );
  return useQuery({
    queryKey: overviewKey(eventId),
    queryFn: async () => (await apiService.getEventOverview(eventId as string)).data as EventOverview,
    enabled: !!eventId,
    staleTime: 30 * 1000,
  });
}

/** Clés de menu qui ont quelque chose à régler (pastille ambre). */
export function stepsNeedingAttention(o?: EventOverview): Set<StepKey> {
  return new Set((o?.steps ?? []).filter((s) => s.status === 'warning').map((s) => s.key));
}
